import { useState, useEffect, useMemo, useCallback } from "react";
import { functions } from "../firebaseConfig";
import { httpsCallable } from "firebase/functions";

import Header from "../components/Header";
import { useDesignVersion } from "../DesignVersionContext";
import PointV2 from "./PointV2";
import common from "../style/screens/Common.module.css";
import style from "../style/screens/Point.module.css";
import ErrMsg from "../ErrMsg";
import { ConfirmButton, ListButton } from "../components/Buttons";
import { SelectorInput } from "../components/Inputs";
import { PointType, usePoint } from "../PointContext";
import { useMember } from "../MemberContext";
import { usePartner } from "../PartnerContext";

export interface MemberType {
    uid: string;
    name: string;
    phone: string;
    deleted: boolean;
    bankAccount: any[];
    savedKiosk: any[];
    timestamp: number;
    partnerId: string;
}

const Point = ({ view = "withdraws" }: { view?: "withdraws" | "methods" }) => {
    const { version } = useDesignVersion();
    const [addPoint, setAddPoint] = useState<boolean>(false);

    if (version === "v2") {
        return <PointV2 view={view} />;
    }

    return (
        <>
            <Header />
            <div className={common["container"]}>
                <div className={common["wrapper"]}>
                    <h4 className={common["text--title"]}>포인트 관리</h4>
                    <p className={common["text--copy"]}>임의의 계정에 포인트를 지급할 수 있습니다.</p>
                    <button className={style["button--add-point"]} onClick={() => setAddPoint(true)}>
                        포인트 지급하기
                    </button>
                    <h4 className={common["text--title"]}>포인트 환전 승인</h4>
                    <p className={common["text--copy"]}>포인트 출금을 승인 할 수 있습니다.</p>
                    <PointTable />
                </div>
            </div>

            {addPoint && <AddPoint close={() => setAddPoint(false)} />}
        </>
    );
};

const PointTable = () => {
    const { withdraws } = usePoint();

    const [loadingConfirmMap, setLoadingConfirmMap] = useState<Record<string, boolean>>({});

    const confirmWithdraw = async (point: PointType) => {
        const pointId = point.id;

        setLoadingConfirmMap((prev) => ({ ...prev, [pointId]: true }));

        return httpsCallable(
            functions,
            "completeWithdraw"
        )({ docId: pointId })
            .then(() => {
                setLoadingConfirmMap((prev) => ({ ...prev, [pointId]: false }));
            })
            .catch((err) => {
                console.error(err);
                setLoadingConfirmMap((prev) => ({ ...prev, [pointId]: false }));

                if (ErrMsg[err.message]) {
                    alert(ErrMsg[err.message]);
                } else {
                    alert("에러가 발생했습니다");
                }
            });
    };

    return (
        <>
            {withdraws && (
                <>
                    <div className={[style["container--rows"], style["container--rows-title"]].join(" ")}>
                        <p className={[style["text--eyebrow"], style["text--eyebrow--title"]].join(" ")}>이름</p>
                        <p className={[style["text--eyebrow"], style["text--eyebrow--title"]].join(" ")}>전화번호</p>
                        <p className={[style["text--eyebrow"], style["text--eyebrow--title"]].join(" ")}>요청일시</p>
                        <p className={[style["text--eyebrow"], style["text--eyebrow--title"]].join(" ")}>요청금액</p>
                        <p
                            className={[
                                style["text--eyebrow"],
                                style["text--eyebrow--title"],
                                style["text--eyebrow--number"],
                            ].join(" ")}
                        >
                            완료버튼
                        </p>
                    </div>
                    <div className={style["wrapper--point-list"]}>
                        {withdraws.map((point, index) => (
                            <div key={point.id} className={[style["unit--point-list"]].join(" ")}>
                                <div className={style["container--rows"]}>
                                    <p className={style["text--eyebrow"]}>{point.member?.name ?? "이름 없음"}</p>
                                    <p className={style["text--eyebrow"]}>{formatPhone(point.member?.phone) ?? "-"}</p>
                                    <p className={style["text--eyebrow"]}>
                                        {new Date(point.timestamp).toLocaleString()}
                                    </p>
                                    <p className={[style["text--eyebrow"]].join(" ")}>
                                        {point.pointDelta ? `₩${point.pointDelta.toLocaleString()}` : "-"}
                                    </p>
                                    <ConfirmButton
                                        text="완료"
                                        loading={loadingConfirmMap[point.id] ?? false}
                                        onClick={() => confirmWithdraw(point)}
                                    />
                                </div>
                            </div>
                        ))}
                    </div>
                </>
            )}
        </>
    );
};

const AddPoint = ({ close }: { close: () => void }) => {
    const { members } = useMember();
    const { partner } = usePartner();

    const [selectedPartnerId, setSelectedPartnerId] = useState<string | null>(null);
    const [search, setSearch] = useState<string>("");

    // partnerId 기반 멤버 필터링 (Notice.tsx 패턴과 동일)
    const filteredMembersByPartner = useMemo(() => {
        if (!selectedPartnerId) return members;
        return members.filter((member) => member.partnerId === selectedPartnerId);
    }, [members, selectedPartnerId]);

    const [active, setActive] = useState<boolean>(false);
    useEffect(() => {
        setActive(true);
    }, []);
    const hide = useCallback(() => {
        setActive(false);
        setTimeout(() => {
            close();
        }, 520);
    }, [close]);

    return (
        <div className={[common["container--overlay"], active && common["container--overlay--active"]].join(" ")}>
            <h4 className={[common["text--title"], common["text--title--overlay"]].join(" ")}>포인트 지급하기</h4>

            <div className={[common["unit--overlay"], active && common["unit--overlay--active"]].join(" ")}>
                <div className={common["wrapper--overlay"]}>
                    <div className={style["unit--overlay"]}>
                        <div className={style["wrapper--overlay--inputs"]}>
                            <h4 className={common["text--overlay"]}>유저 정보 입력</h4>

                            {/* 파트너 선택 (Notice.tsx와 동일한 UI/동작) */}
                            <div className={style["container--partner-selector"]}>
                                <SelectorInput
                                    placeholder="파트너사 선택"
                                    value={selectedPartnerId || ""}
                                    setValue={setSelectedPartnerId}
                                >
                                    {partner.map((p) => (
                                        <option key={p.docId} value={p.id}>
                                            {p.name}
                                        </option>
                                    ))}
                                </SelectorInput>
                            </div>

                            {selectedPartnerId ? (
                                <>
                                    <input
                                        className={style["input--member-filter"]}
                                        type="text"
                                        placeholder="이름, 전화번호 또는 파트너사 아이디로 검색"
                                        onChange={(e) => {
                                            setSearch(e.target.value);
                                        }}
                                    />
                                    <div className={style["container--members"]}>
                                        <div className={style["unit--member-title"]}>
                                            <p className={style["text--member"]}>이름</p>
                                            <p className={style["text--member"]}>전화번호</p>
                                            <p className={style["text--member"]}>포인트 입력</p>
                                        </div>
                                        {filteredMembersByPartner
                                            .filter((member) => {
                                                return (
                                                    search === "" ||
                                                    (search &&
                                                        (member.partnerId
                                                            .toLowerCase()
                                                            .includes(search.toLowerCase()) ||
                                                            member.name.toLowerCase().includes(search.toLowerCase()) ||
                                                            member.phone.toLowerCase().includes(search.toLowerCase()) ||
                                                            formatPhone(member.phone)!
                                                                .toLowerCase()
                                                                .includes(search.toLowerCase())))
                                                );
                                            })
                                            .map((member) => (
                                                <MemberList
                                                    key={member.uid}
                                                    member={member}
                                                    partnerId={selectedPartnerId}
                                                />
                                            ))}
                                    </div>
                                </>
                            ) : (
                                <div className={common["text--alert"]}>
                                    <p>파트너사를 선택해주세요.</p>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
                <div className={common["wrapper--overlay--footer"]}>
                    <ListButton type="Default" text="취소" onClick={hide} />
                </div>
            </div>
        </div>
    );
};

const MemberList = ({ member, partnerId }: { member: MemberType; partnerId: string | null }) => {
    const [point, setPoint] = useState<number | null>(null);
    const [loading, setLoading] = useState<boolean>(false);

    const givePoint = async () => {
        if (!point) {
            alert("지급할 포인트를 입력해주세요.");
            return;
        }

        if (!partnerId) {
            alert("파트너를 선택해주세요.");
            return;
        }

        setLoading(true);

        // 선택된 partnerId 사용 (하드코딩 제거)
        return httpsCallable(
            functions,
            "addPoint"
        )({ uid: member.uid, pointDelta: point, partnerId: partnerId })
            .then(() => {
                setPoint(null);
                setLoading(false);
                alert("포인트가 지급되었습니다.");
                return true;
            })
            .catch((err) => {
                console.error(err);
                setLoading(false);

                if (ErrMsg[err.message]) {
                    alert(ErrMsg[err.message]);
                } else {
                    alert("에러가 발생했습니다");
                }
            });
    };

    return (
        <div className={style["unit--member-list"]}>
            <p className={style["text--member"]}>
                {member.name}
                {member.name.length > 3 && <span>{member.name}</span>}
            </p>
            <p className={style["text--member"]}>{member.phone ? formatPhone(member.phone) : "전화번호 없음"}</p>
            <input
                type="number"
                className={style["input--member-point"]}
                placeholder="지급할 포인트 입력"
                value={point ? point : ""}
                onChange={(e) => {
                    setPoint(e.target.value ? parseInt(e.target.value) : 0);
                }}
            ></input>
            <ConfirmButton loading={loading} onClick={givePoint} text="지급" />
        </div>
    );
};

const formatPhone = (phone: string | undefined) => {
    const cleaned = ("" + phone).replace(/\D/g, "");
    const match = cleaned.match(/^(\d{3})(\d{4})(\d{4})$/);
    if (match) {
        return `${match[1]}-${match[2]}-${match[3]}`;
    }
    return phone;
};

export default Point;
